import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Chip,
  OutlinedInput,
  SelectChangeEvent,
  Grid,
  CircularProgress
} from '@mui/material';
import { CreateUserRequest, UserRole } from '../../models/User';
import { useAuth } from '../../hooks/useAuth';
import { Hotel } from '../../models/Hotel';
import { Zone } from '../../models/Zone';
import * as hotelService from '../../services/hotelService';
import * as zoneService from '../../services/newZoneService';

interface UserFormProps {
  onSubmit: (userData: CreateUserRequest) => Promise<void>;
}

const UserForm: React.FC<UserFormProps> = ({ onSubmit }) => {
  const { userRole, user } = useAuth();
  const currentUserRole = userRole || UserRole.HotelUser;

  const [formData, setFormData] = useState<CreateUserRequest>({
    username: '',
    password: '',
    role: UserRole.HotelUser,
    hotelId: user?.hotelId,
    assignedZoneIds: [],
  } as CreateUserRequest);

  const [hotels, setHotels] = useState<Hotel[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadHotels = async () => {
      if (currentUserRole === UserRole.HotelManager) {
        try {
          const data = await hotelService.getHotels();
          setHotels(data);
        } catch (err) {
          console.error('Failed to load hotels', err);
        }
      }
    };
    loadHotels();
  }, [currentUserRole]);

  useEffect(() => {
    const loadZones = async () => {
      try {
        setLoading(true);
        if (formData.hotelId) {
          const data = await zoneService.getZonesByHotel(formData.hotelId);
          setZones(data);
        }
      } catch (err) {
        console.error('Failed to load zones', err);
      } finally {
        setLoading(false);
      }
    };
    if (formData.role === UserRole.HotelUser && formData.hotelId) {
      loadZones();
    }
  }, [formData.hotelId, formData.role]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name as string]: value });
    if (name === 'role') {
      const role = value as UserRole;
      if (role === UserRole.HotelManager) {
        setFormData(prev => ({ ...prev, role, assignedZoneIds: [] }));
      }
    }
    if (name === 'hotelId') {
      setFormData(prev => ({ ...prev, [name as string]: value, assignedZoneIds: [] }));
    }
  };

  const handleZoneChange = (event: SelectChangeEvent<number[]>) => {
    const { value } = event.target;
    setFormData({
      ...formData,
      assignedZoneIds: typeof value === 'string' ? [parseInt(value, 10)] : value as number[],
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      setSubmitting(true);
      await onSubmit(formData);
      setFormData({
        username: '', password: '',
        role: UserRole.HotelUser, hotelId: user?.hotelId, assignedZoneIds: [],
      });
    } catch (err) {
      console.error('Error submitting form:', err);
      const error = err as any;
      if (typeof error.response?.data === 'string') {
        setError(error.response.data);
      } else if (error.response?.data?.title) {
        setError(error.response.data.title);
      } else if (error.message) {
        setError(error.message);
      } else {
        setError('Kullanıcı oluşturulamadı. Lütfen tekrar deneyin.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            required fullWidth size="small" name="username" label="Kullanıcı Adı"
            value={formData.username} onChange={handleChange}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            required fullWidth size="small" name="password" label="Şifre" type="password"
            value={formData.password} onChange={handleChange}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Rol</InputLabel>
            <Select name="role" value={formData.role} label="Rol"
              onChange={handleChange as any} sx={{ borderRadius: '10px' }}>
              {currentUserRole === UserRole.HotelManager && (
                <MenuItem value={UserRole.HotelManager}>Hotel Manager</MenuItem>
              )}
              <MenuItem value={UserRole.HotelUser}>Hotel Staff</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {currentUserRole === UserRole.HotelManager && formData.role !== UserRole.HotelManager && (
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small" required>
              <InputLabel>Hotel</InputLabel>
              <Select name="hotelId" value={formData.hotelId || ''} label="Hotel"
                onChange={handleChange as any} sx={{ borderRadius: '10px' }}>
                {hotels.map((hotel) => (
                  <MenuItem key={hotel.id} value={hotel.id}>{hotel.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        {formData.role === UserRole.HotelUser && formData.hotelId && (
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Atanmış Zone'lar</InputLabel>
              <Select
                multiple name="assignedZoneIds" value={formData.assignedZoneIds || []}
                onChange={handleZoneChange}
                input={<OutlinedInput label="Atanmış Zone'lar" />}
                sx={{ borderRadius: '10px' }}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as number[]).map((zoneId) => {
                      const zone = zones.find(z => z.id === zoneId);
                      return <Chip key={zoneId} label={zone?.name || zoneId} size="small"
                        sx={{ height: 22, fontSize: '0.75rem', borderRadius: '6px' }} />;
                    })}
                  </Box>
                )}
              >
                {zones.map((zone) => (
                  <MenuItem key={zone.id} value={zone.id}>{zone.name}</MenuItem>
                ))}
              </Select>
              <FormHelperText>Bu kullanıcının erişebileceği zone'ları seçin</FormHelperText>
            </FormControl>
          </Grid>
        )}

        {error && (
          <Grid item xs={12}>
            <FormHelperText error>{error}</FormHelperText>
          </Grid>
        )}

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="submit" disabled={loading || submitting}
              sx={{
                textTransform: 'none', fontWeight: 600, fontSize: '0.85rem',
                borderRadius: '10px', px: 3,
                backgroundColor: '#4294e3', color: '#fff',
                '&:hover': { backgroundColor: '#3680c9' },
                '&.Mui-disabled': { backgroundColor: '#e0e0e0', color: '#999' }
              }}
            >
              {submitting ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Kullanıcı Oluştur'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UserForm;
